/**
 * FarmFlix brand tokens — the single source of truth for BOTH MUI themes
 * during the PM5 v4 → v5+ coexistence era (see docs/epics/pm5-mui-v5-migration.md,
 * T0 / risk 4: "brand values are never edited on one side only").
 *
 * Consumed by:
 *   - src/components/Theme.js  (legacy MUI v4 `createMuiTheme`)
 *   - src/theme/themeV5.ts     (MUI v9 `createTheme`)
 *
 * Deliberately depends on NEITHER MUI major — these are plain typed constants,
 * so nothing here has to change when v4 is finally uninstalled (T6).
 *
 * Every value below was lifted verbatim from the pre-T0 Theme.js. Do not
 * "tidy" a value (e.g. `#fff` → `#ffffff`) — the two spellings are kept apart
 * on purpose so the v4 theme keeps emitting byte-identical CSS.
 */

/* ------------------------------------------------------------------------ *
 * Colour
 * ------------------------------------------------------------------------ */

export const color = {
  /* Neutrals */
  black: "#000",
  /** Short-form white, as spelled in `palette.common.white` / `h4`. */
  white: "#fff",
  /** Long-form white, as spelled in `h1` / `h2` / `subtitle1`. Same colour,
   *  kept separate so the emitted CSS is unchanged. */
  whiteLong: "#ffffff",
  gray: "#eee",
  darkGray: "#bbb",
  lightGray: "#e9e9e9",
  offWhite: "#fafafa",

  /* Brand */
  /** FarmFlix lime — the primary brand colour. */
  ffGreen: "#99ca3c",
  /** Lime hover state (buttons). */
  ffGreenHover: "#a2d641",
  /** FarmFlix navy — the primary brand ground. */
  ffNavy: "#171717",
  ffNavyLight: "#202020",
  ffNavyLighter: "#292929",
  /** AgLife sub-brand blue. */
  agLife: "#2F5FB0",
  /** Material blue-grey 900, kept for legacy `background.navy` consumers. */
  navy: "#263238",
  /** `primary.dark` — amber, not a shade of the lime. Intentional. */
  brandAmber: "#ffa400",
  primaryLight: "rgba(207, 232, 65, 1)",
  primaryContrastText: "rgba(23, 3, 3, 1)",

  /* Upgrade / paywall surfaces */
  upgrade: "#3aa9de",
  upgradeHover: "#3cb5ef",
  upgradeAlt: "#797979",
  upgradeBG: "#585858",
  /** `palette.common.upgrade` — a near-white, unrelated to the blues above. */
  upgradeLight: "#f1f1f1",

  /* Secondary (blue) */
  secondaryLight: "rgba(83, 187, 243, 1)",
  secondaryMain: "rgba(0, 160, 245, 1)",
  secondaryDark: "rgba(17, 133, 194, 1)",
  /** Secondary-contained button fill — distinct from `secondaryMain`. */
  buttonSecondary: "#55b6e4",
  buttonSecondaryHover: "#3aaee6",

  /* Error */
  errorLight: "#e57373",
  errorMain: "#c53737",
  errorDark: "#d32f2f",

  /* Text (on light) */
  textPrimary: "rgba(0, 0, 0, 0.87)",
  textSecondary: "rgba(0, 0, 0, 0.54)",
  /** Used for both `text.disabled` and the (v4-only) `text.hint`. */
  textDisabled: "rgba(0, 0, 0, 0.38)",
  /** Outlined-button hover wash. */
  blackWash: "rgba(0, 0, 0, 0.05)",

  /* Dark-surface neutrals used by the component overrides */
  darkSurface: "#444444",
  darkSurfaceText: "#9e9e9e",
  stepIconIdle: "#a0a0a0",
  inputBorder: "#a5a5a5",
} as const;

/* ------------------------------------------------------------------------ *
 * Spacing
 * ------------------------------------------------------------------------ */

/** MUI spacing unit, in px. `theme.spacing(2)` === 16px on both themes. */
export const spacingUnit = 8;

/* ------------------------------------------------------------------------ *
 * Breakpoints
 * ------------------------------------------------------------------------ */

/**
 * The MUI **v4** breakpoint values. v5+ shipped different defaults
 * (sm 600 / md 900 / lg 1200 / xl 1536), so the v5 theme pins these
 * explicitly — every hand-written media query in this codebase, and the
 * `mq` strings below, assume the v4 grid.
 */
export const breakpointValues = {
  xs: 0,
  sm: 600,
  md: 960,
  lg: 1280,
  xl: 1920,
} as const;

/**
 * The two raw media-query strings the theme itself writes. Written out in
 * full rather than interpolated so the emitted CSS text is trivially
 * greppable and provably unchanged; they mirror `breakpointValues.sm` / `.md`.
 */
export const mq = {
  sm: "@media (min-width:600px)",
  md: "@media (min-width:960px)",
} as const;

/* ------------------------------------------------------------------------ *
 * Type
 * ------------------------------------------------------------------------ */

export const fontStack: string[] = [
  "Roboto",
  '"Helvetica Neue"',
  "Arial",
  "sans-serif",
];

export const fontFamily: string = fontStack.join(",");

/** Weights are strings because that is how the v4 theme spelled them. */
export const fontWeight = {
  regular: "400",
  medium: "500",
} as const;

/* ------------------------------------------------------------------------ *
 * Composed theme fragments — spread verbatim into BOTH themes
 * ------------------------------------------------------------------------ */

/**
 * The palette, exactly as the v4 theme declared it — including the
 * non-standard keys (`common.gray`, `background.ffGreen`, …) the app reads
 * all over the place, and `text.hint`, which v5+ dropped from `TypeText`
 * but which is harmless to keep supplying.
 */
export const paletteTokens = {
  common: {
    black: color.black,
    white: color.white,
    gray: color.gray,
    darkgray: color.darkGray,
    lightgray: color.lightGray,
    dark: color.ffNavy,
    upgrade: color.upgradeLight,
  },
  background: {
    paper: color.white,
    default: color.offWhite,
    ffGreen: color.ffGreen,
    ffNavy: color.ffNavy,
    ffNavyLighter: color.ffNavyLighter,
    ffNavyLight: color.ffNavyLight,
    agLife: color.agLife,
    navy: color.navy,
    dark: color.ffNavy,
    upgrade: color.upgrade,
    upgradeHover: color.upgradeHover,
    upgradeAlt: color.upgradeAlt,
    upgradeBG: color.upgradeBG,
  },
  primary: {
    light: color.primaryLight,
    main: color.ffGreen,
    dark: color.brandAmber,
    contrastText: color.primaryContrastText,
  },
  secondary: {
    light: color.secondaryLight,
    main: color.secondaryMain,
    dark: color.secondaryDark,
    contrastText: color.white,
  },
  error: {
    light: color.errorLight,
    main: color.errorMain,
    dark: color.errorDark,
    contrastText: color.white,
  },
  text: {
    primary: color.textPrimary,
    secondary: color.textSecondary,
    disabled: color.textDisabled,
    hint: color.textDisabled,
  },
};

/**
 * The typography scale, exactly as the v4 theme declared it — including the
 * two non-standard variants the app relies on (`bodysml`, `a`).
 */
export const typographyTokens = {
  fontFamily,
  h1: {
    fontSize: "2rem",
    fontWeight: fontWeight.medium,
    color: color.whiteLong,
    [mq.sm]: {
      fontSize: "2.8rem",
      lineHeight: "3rem",
    },
    [mq.md]: {
      fontSize: "3.4rem",
      lineHeight: "3.6rem",
    },
  },
  h2: {
    fontSize: "2.4rem",
    fontWeight: fontWeight.medium,
    color: color.whiteLong,
  },
  h3: {
    fontSize: "1.6rem",
    fontWeight: fontWeight.medium,
    color: color.ffGreen,
    [mq.sm]: {
      fontSize: "1.6rem",
      lineHeight: "1.7rem",
    },
    [mq.md]: {
      fontSize: "1.8rem",
      lineHeight: "2rem",
    },
  },
  h4: {
    fontSize: "1.2rem",
    lineHeight: "1.3rem",
    color: color.white,
    [mq.sm]: {
      fontSize: "1.4rem",
      lineHeight: "1.5rem",
    },
    [mq.md]: {
      fontSize: "1.5rem",
      lineHeight: "1.6rem",
    },
  },
  h5: {},
  h6: {},
  subtitle1: {
    fontWeight: fontWeight.medium,
    color: color.whiteLong,
    fontSize: "1.6rem",
    lineHeight: "1.8rem",
    [mq.sm]: {
      fontSize: "1.2rem",
      lineHeight: "1.3rem",
    },
    [mq.md]: {
      fontSize: "1.6rem",
      lineHeight: "1.7rem",
    },
  },
  subtitle2: {},
  body1: {
    color: color.lightGray,
    fontWeight: fontWeight.regular,
    fontSize: "1rem",
    lineHeight: "1.2rem",
  },
  body2: {
    color: color.lightGray,
    fontWeight: fontWeight.regular,
    fontSize: "0.875rem",
    lineHeight: "1rem",
  },
  bodysml: {
    fontSize: "0.6rem",
  },
  button: {
    backgroundColor: color.ffGreen,
  },
  a: {
    textDecoration: "none",
  },
};

/* ------------------------------------------------------------------------ *
 * Component metrics
 * ------------------------------------------------------------------------ */

/**
 * The non-colour literals used by the component override blocks. Not "brand
 * tokens" in the strict sense, but both themes need the identical numbers, so
 * they live here rather than being typed twice (PM5 risk 4).
 */
export const componentMetrics = {
  button: {
    borderWidth: "2px",
    borderRadius: "25px",
    margin: "0 5px",
    transition: "0.25s",
    lineHeight: "1rem",
    textPadding: "12px 20px",
    outlinedPadding: "12px 20px",
    smallPadding: "6px 20px",
    largePadding: "12px 40px",
    largeFontSize: "1rem",
    largeFontSizeUp: "1.1rem",
    largeLineHeightSm: "1.3rem",
    largeMargin: "10px 0 0 0",
  },
  divider: {
    height: "5px",
  },
  tableCell: {
    fontWeight: "400",
    padding: "12px",
  },
  formControlLabel: {
    fontWeight: 500,
    fontSize: "1.2rem",
    width: "100%",
  },
  formLabel: {
    lineHeight: 0.5,
    fontWeight: 500,
    fontSize: "1.1rem",
  },
  stepper: {
    padding: 0,
    paddingUp: "24px 0",
  },
  stepIcon: {
    fontSize: "1.6rem",
  },
  stepLabel: {
    fontSize: "0.8rem",
    fontSizeUp: "1rem",
    lineHeight: "0",
  },
  input: {
    marginTop: "6px",
    fontSize: "0.9rem",
    lineHeight: "1.2rem",
    padding: "14px 10px 12px 10px",
    borderWidth: "1px",
    borderRadius: "5px",
  },
  outlinedInput: {
    padding: "13.5px 14px",
    fontSize: "0.9rem",
  },
  alert: {
    messagePadding: "10px 0",
  },
  tooltip: {
    popperZIndex: 1100,
  },
  select: {
    marginTop: 0,
  },
} as const;
